'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/navigation';
import {
  ChevronDown as LucideChevronDown, ChevronLeft, ChevronRight,
  CheckCircle2, X,
  Star,
  ShieldCheck,
  Loader2,
} from 'lucide-react';
import siteTheme from '@/lib/theme';
import type { CmsRoom } from '@/lib/cms';
import { ButtonSpinner } from '@/components/ButtonSpinner';
import { buildSignedBookingUrl } from '@/lib/booking-navigate';

async function fetchAvailableIds(checkIn: string, checkOut: string): Promise<Set<number>> {
  const res = await fetch('/api/sandy/availability', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ checkIn, checkOut }),
  });
  if (!res.ok) throw new Error(`availability check failed: ${res.status}`);
  const data = await res.json();
  return new Set<number>((data.availableRoomTypeIds ?? []).map(Number));
}

const { fonts, colors } = siteTheme;

const C = {
  navy:        '#17324d',
  borderLight: '#e7dfd0',
  borderField: '#ddd6c8',
  borderRow:   '#e8e1d5',
  borderInner: '#ece7db',
  muted:       '#666',
  muted2:      '#7b7b7b',
  green:       '#1f8d4f',
  greenBg:     '#e6f6ea',
  white:       '#ffffff',
  goldPill:    '#f7efe0',
  noteBg:      '#faf8f3',
};

const CSS = `
@keyframes wgtIn {
  from { opacity:0; transform:translateY(8px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes spin { to { transform: rotate(360deg); } }
.wgt-panel { animation: wgtIn 0.3s cubic-bezier(0.22,1,0.36,1) both; }
.wgt-delay { animation: wgtIn 0.4s ease both; animation-delay:0.7s; opacity:0; animation-fill-mode:forwards; }
`;

const panelStyle: React.CSSProperties = {
  background:           'rgba(240,235,225,0.85)',
  backdropFilter:       'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  borderRadius:         '28px',
  border:               '1px solid rgba(255,255,255,0.6)',
  boxShadow:            '0 8px 40px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.85)',
  width:                '100%',
  padding:              'clamp(1.25rem,4vw,1.5rem)',
};

const glassPanel: React.CSSProperties = {
  position:             'absolute',
  zIndex:               200,
  background:           'rgba(248,245,239,0.98)',
  backdropFilter:       'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border:               `1px solid ${C.borderField}`,
  borderRadius:         '14px',
  boxShadow:            '0 12px 40px rgba(0,0,0,0.16)',
};


const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Mo','Tu','We','Th','Fr','Sa','Su'];

type Step = 'form' | 'results' | 'selected' | 'confirmed';

function useClickOutside(ref: React.RefObject<HTMLElement|null>, cb: ()=>void, on: boolean) {
  useEffect(() => {
    if (!on) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) cb(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, cb, on]);
}
function isoToLocal(iso: string) { return iso ? new Date(iso+'T00:00:00') : null; }
function toISO(d: Date) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
function fmtShort(iso: string) { const d = isoToLocal(iso); if (!d) return ''; return d.toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}); }

function FieldCard({ label, value, onClick }: { label: string; value: string; onClick?: ()=>void }) {
  return (
    <button type="button" onClick={onClick}
      style={{ background:'rgba(255,255,255,0.5)',
        borderRadius:'10px',
        padding:'0.7rem 0.85rem', width:'100%', cursor:onClick?'pointer':'default', textAlign:'left' }}>
      <p style={{ fontFamily:fonts.sans, fontSize:'1.1rem', fontWeight:600, letterSpacing:'0.1em',
        textTransform:'uppercase', color:C.muted2, marginBottom:'0.3rem' }}>{label}</p>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        fontFamily:fonts.sans, fontSize:'1.4rem', fontWeight:400, color:C.navy }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{value}</span>
        {onClick && <LucideChevronDown size={12} color={C.muted2} strokeWidth={2} style={{ flexShrink:0, marginLeft:'0.25rem' }} />}
      </div>
    </button>
  );
}

function CounterRow({ label, sub, value, onChange, min=0, max=10 }: {
  label: string; sub?: string; value: number; onChange: (v:number)=>void; min?: number; max?: number;
}) {
  const btn = (dis: boolean): React.CSSProperties => ({
    width:'2.4rem', height:'2.4rem', border:`1px solid rgba(23,50,77,${dis?'0.1':'0.22'})`,
    borderRadius:'50%', background:'transparent', color:C.navy,
    fontSize:'1.5rem', lineHeight:1, display:'flex', alignItems:'center', justifyContent:'center',
    cursor:dis?'default':'pointer', opacity:dis?0.28:1,
  });
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.55rem 0', gap:'1rem' }}>
      <div>
        <p style={{ fontFamily:fonts.sans, fontSize:'1.4rem', fontWeight:500, color:C.navy }}>{label}</p>
        {sub && <p style={{ fontFamily:fonts.sans, fontSize:'1.2rem', color:'rgba(23,50,77,0.45)' }}>{sub}</p>}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', flexShrink:0 }}>
        <button type="button" onClick={()=>onChange(Math.max(min,value-1))} style={btn(value<=min)}>−</button>
        <span style={{ fontFamily:fonts.sans, fontSize:'1.6rem', fontWeight:600, color:C.navy, minWidth:'1.6rem', textAlign:'center' }}>{value}</span>
        <button type="button" onClick={()=>onChange(Math.min(max,value+1))} style={btn(value>=max)}>+</button>
      </div>
    </div>
  );
}

const cdivider = <div style={{ height:'1px', background:'rgba(23,50,77,0.07)' }} />;

function DoneBtn({ onClick }: { onClick: ()=>void }) {
  return (
    <button type="button" onClick={onClick} className="btn-primary btn-sm"
      style={{ marginTop:'0.85rem', width:'100%' }}>
      Done
    </button>
  );
}

function RoomsDropdown({ count, onChange }: { count: number; onChange: (n:number)=>void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, ()=>setOpen(false), open);
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <FieldCard label="Rooms" value={`${count} Room${count!==1?'s':''}`} onClick={()=>setOpen(o=>!o)} />
      {open && (
        <div style={{ ...glassPanel, top:'calc(100% + 0.4rem)', left:0, right:0, padding:'0.2rem 1.2rem 1.1rem' }}>
          <CounterRow label="Rooms" value={count} onChange={onChange} min={1} max={10} />
          <DoneBtn onClick={()=>setOpen(false)} />
        </div>
      )}
    </div>
  );
}

function GuestsDropdown({ adults, children: ch, onAdults, onChildren, maxAdults, maxChildren, roomCount }: {
  adults: number; children: number; onAdults: (v:number)=>void; onChildren: (v:number)=>void;
  maxAdults: number; maxChildren: number; roomCount: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, ()=>setOpen(false), open);
  const val = `${adults} Adult${adults!==1?'s':''}${ch>0?` · ${ch} Child${ch!==1?'ren':''}` :''}`;
  const capA = roomCount * maxAdults;
  const capC = roomCount * maxChildren;
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <FieldCard label="Guests" value={val} onClick={()=>setOpen(o=>!o)} />
      {open && (
        <div style={{ ...glassPanel, top:'calc(100% + 0.4rem)', left:0, right:0, padding:'0.4rem 1.2rem 1.1rem' }}>
          <CounterRow label="Adults" value={adults} onChange={onAdults} min={1} max={capA} />
          {cdivider}
          <CounterRow label="Children" sub="0–17 Years Old" value={ch} onChange={onChildren} max={capC} />
          <p style={{ fontFamily:fonts.sans, fontSize:'1.1rem', color:'rgba(23,50,77,0.42)', textAlign:'center', padding:'0.5rem 0 0.2rem' }}>
            Max {maxAdults} adult{maxAdults!==1?'s':''} · {maxChildren} child{maxChildren!==1?'ren':''} per room
          </p>
          <DoneBtn onClick={()=>setOpen(false)} />
        </div>
      )}
    </div>
  );
}

function DatePicker({ label, value, onChange, min, alignRight=false }: {
  label: string; value: string; onChange: (iso:string)=>void; min?: string; alignRight?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, ()=>setOpen(false), open);
  const sel   = isoToLocal(value);
  const today = new Date(); today.setHours(0,0,0,0);
  const minD  = min ? isoToLocal(min) : null;
  const [viewY, setViewY] = useState(sel?.getFullYear()??today.getFullYear());
  const [viewM, setViewM] = useState(sel?.getMonth()??today.getMonth());
  useEffect(()=>{ const d=isoToLocal(value); if(d){setViewY(d.getFullYear());setViewM(d.getMonth());} }, [value]);
  function prevM() { if(viewM===0){setViewM(11);setViewY(y=>y-1);}else setViewM(m=>m-1); }
  function nextM() { if(viewM===11){setViewM(0);setViewY(y=>y+1);}else setViewM(m=>m+1); }
  const first  = new Date(viewY,viewM,1);
  const days   = new Date(viewY,viewM+1,0).getDate();
  const offset = (first.getDay()+6)%7;
  const cells: (number|null)[] = [...Array(offset).fill(null), ...Array.from({length:days},(_,i)=>i+1)];
  while (cells.length%7!==0) cells.push(null);
  const isDis = (d: number) => !!(minD && new Date(viewY,viewM,d) < minD);
  const isSel = (d: number) => !!(sel && sel.getFullYear()===viewY && sel.getMonth()===viewM && sel.getDate()===d);
  const isTdy = (d: number) => today.getFullYear()===viewY && today.getMonth()===viewM && today.getDate()===d;
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <FieldCard label={label} value={value ? fmtShort(value) : 'Select date'} onClick={()=>setOpen(o=>!o)} />
      {open && (
        <div style={{ ...glassPanel, bottom:'calc(100% + 0.4rem)',
          left:alignRight?'auto':0, right:alignRight?0:'auto', padding:'1.1rem', width:'22rem', zIndex:300 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.9rem' }}>
            <button type="button" onClick={prevM}
              style={{ background:'none', border:'none', cursor:'pointer', padding:'0.4rem', display:'flex', borderRadius:'50%' }}>
              <ChevronLeft size={13} color={C.navy} strokeWidth={1.5} />
            </button>
            <span style={{ fontFamily:fonts.serif, fontWeight:300, fontSize:'1.6rem', color:C.navy, letterSpacing:'0.04em' }}>
              {MONTHS[viewM]}, {viewY}
            </span>
            <button type="button" onClick={nextM}
              style={{ background:'none', border:'none', cursor:'pointer', padding:'0.4rem', display:'flex', borderRadius:'50%' }}>
              <ChevronRight size={13} color={C.navy} strokeWidth={1.5} />
            </button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:'0.25rem' }}>
            {DAYS.map(d=>(
              <div key={d} style={{ fontFamily:fonts.sans, fontSize:'1.2rem', fontWeight:600,
                color:'rgba(23,50,77,0.4)', textAlign:'center', padding:'0.2rem 0' }}>{d}</div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'0.1rem' }}>
            {cells.map((d,i)=>{
              if (d===null) return <div key={i}/>;
              const dis=isDis(d), s=isSel(d), t=isTdy(d);
              return (
                <button key={i} type="button" onClick={()=>{ if(!dis){ onChange(toISO(new Date(viewY,viewM,d))); setOpen(false); }}}
                  style={{ height:'2.6rem', border:t&&!s?'1px solid rgba(23,50,77,0.3)':'none',
                    borderRadius:'50%', backgroundColor:s?colors.navyDeep:'transparent',
                    color:s?C.white:dis?'rgba(23,50,77,0.2)':C.navy,
                    fontFamily:fonts.sans, fontSize:'1.4rem', cursor:dis?'default':'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center', fontWeight:t?600:300 }}
                  onMouseEnter={e=>{ if(!dis&&!s) e.currentTarget.style.backgroundColor='rgba(23,50,77,0.07)'; }}
                  onMouseLeave={e=>{ if(!s) e.currentTarget.style.backgroundColor='transparent'; }}
                >{d}</button>
              );
            })}
          </div>
          <div style={{ marginTop:'0.7rem', textAlign:'right', borderTop:`1px solid ${C.borderInner}`, paddingTop:'0.6rem' }}>
            <button type="button"
              onClick={()=>{ if(!minD||today>=minD){ onChange(toISO(today)); setOpen(false); }}}
              style={{ background:'none', border:'none', cursor:'pointer', fontFamily:fonts.sans,
                fontSize:'1.4rem', color:colors.gold, fontWeight:500 }}>Today</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PanelHeading({ title, onClose }: { title: string; onClose?: ()=>void }) {
  return (
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
      <div>
        <p style={{ fontFamily:fonts.script, fontSize:'clamp(2rem,3.5vw,2.6rem)', color:colors.gold,
          lineHeight:1.2, fontStyle:'italic' }}>
          Check Availability
        </p>
        <h2 style={{ fontFamily:fonts.serif, fontSize:'clamp(2rem,3.8vw,2.6rem)', fontWeight:400,
          letterSpacing:'0.12em', textTransform:'uppercase', color:C.navy, marginTop:'0.35rem', lineHeight:1.2 }}>
          {title}
        </h2>
      </div>
      {onClose && (
        <button type="button" onClick={onClose}
          style={{ width:'2.6rem', height:'2.6rem', borderRadius:'50%', border:`1px solid ${C.borderField}`,
            background:'rgba(255,255,255,0.5)', display:'flex', alignItems:'center', justifyContent:'center',
            cursor:'pointer', color:C.navy, flexShrink:0, marginLeft:'0.5rem', marginTop:'0.1rem' }}>
          <X size={14} strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

const SecDiv = () => <div style={{ height:'1px', background:C.borderLight, margin:'1.1rem 0' }} />;

function TrustBadges() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'1.6rem', marginTop:'1.1rem' }}>
      <span style={{ display:'flex', alignItems:'center', gap:'0.45rem', fontFamily:fonts.sans, fontSize:'1.1rem', color:'rgba(13,47,68,0.5)' }}>
        <ShieldCheck size={13} color="rgba(13,47,68,0.45)" strokeWidth={1.4} />
        Secure booking
      </span>
      <span style={{ width:'1px', height:'1.2rem', background:'rgba(13,47,68,0.12)' }} />
      <span style={{ display:'flex', alignItems:'center', gap:'0.45rem', fontFamily:fonts.sans, fontSize:'1.1rem', color:'rgba(13,47,68,0.5)' }}>
        <Star size={13} color="rgba(13,47,68,0.45)" strokeWidth={1.4} />
        Best price guaranteed
      </span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Main widget
   ══════════════════════════════════════════════════════ */

/* ── Capacity helpers ── */
function maxCapacity(rooms: CmsRoom[]) {
  const maxA = rooms.length ? Math.max(...rooms.map(r => r.max_adults   ?? 2)) : 2;
  const maxC = rooms.length ? Math.max(...rooms.map(r => r.max_children ?? 1)) : 1;
  return { maxA: Math.max(1, maxA), maxC: Math.max(0, maxC) };
}
function neededRooms(adults: number, children: number, maxA: number, maxC: number): number {
  const fromA = Math.ceil(adults / maxA);
  const fromC = maxC > 0 && children > 0 ? Math.ceil(children / maxC) : 0;
  return Math.max(1, fromA, fromC);
}

/* Distribute guests greedily across confirmed rooms, respecting per-room capacity */
function distributeGuests(rooms: CmsRoom[], totalAdults: number, totalChildren: number) {
  let remA = totalAdults, remC = totalChildren;
  return rooms.map(r => {
    const a = Math.min(r.max_adults   ?? 2, remA); remA -= a;
    const c = Math.min(r.max_children ?? 1, remC); remC -= c;
    return { adults: a, children: c };
  });
}

const layoutTransition = { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const };

export default function CheckAvailabilityType1({ rooms: roomTypes = [] }: { rooms?: CmsRoom[] }) {
  const router   = useRouter();
  const today    = toISO(new Date());
  const tomorrow = toISO(new Date(Date.now()+86_400_000));

  const [roomCount,      setRoomCount]      = useState(1);
  const [simpleAdults,   setSimpleAdults]   = useState(1);
  const [simpleChildren, setSimpleChildren] = useState(0);

  const { maxA: maxAdultsPerRoom, maxC: maxChildrenPerRoom } = maxCapacity(roomTypes);

  function handleSetAdults(v: number) {
    setSimpleAdults(v);
    const needed = neededRooms(v, simpleChildren, maxAdultsPerRoom, maxChildrenPerRoom);
    if (needed > roomCount) setRoomCount(needed);
  }
  function handleSetChildren(v: number) {
    setSimpleChildren(v);
    const needed = neededRooms(simpleAdults, v, maxAdultsPerRoom, maxChildrenPerRoom);
    if (needed > roomCount) setRoomCount(needed);
  }
  function handleSetRoomCount(v: number) {
    setRoomCount(v);
    const capA = v * maxAdultsPerRoom;
    const capC = v * maxChildrenPerRoom;
    if (simpleAdults   > capA) setSimpleAdults(capA);
    if (simpleChildren > capC) setSimpleChildren(capC);
  }
  const [checkin,        setCheckin]        = useState('');
  const [checkout,       setCheckout]       = useState('');
  const [loading,        setLoading]        = useState(false);
  const [noRooms,        setNoRooms]        = useState(false);
  const [step,           setStep]           = useState<Step>('form');
  const [availableRoomIds, setAvailableRoomIds] = useState<Set<number>>(new Set());
  const [selectedRoom,   setSelectedRoom]   = useState<CmsRoom|null>(null);
  const [booking,        setBooking]        = useState(false);

  useEffect(()=>{ setCheckin(toISO(new Date())); setCheckout(toISO(new Date(Date.now()+86_400_000))); }, []);

  const isMultiFlow = roomCount > 1;
  const minCheckout = checkin ? toISO(new Date(new Date(checkin+'T00:00:00').getTime()+86_400_000)) : tomorrow;
  const availRooms  = roomTypes.filter(rt => rt.external_room_type_id != null && availableRoomIds.has(Number(rt.external_room_type_id)));
  const totalGuests = simpleAdults + simpleChildren;

  function nightsCount() {
    if (!checkin || !checkout) return 1;
    return Math.max(1, Math.round((new Date(checkout+'T00:00:00').getTime() - new Date(checkin+'T00:00:00').getTime()) / 86400000));
  }

  function handleCheckinChange(val: string) {
    setCheckin(val); setNoRooms(false);
    if (checkout <= val) setCheckout(toISO(new Date(new Date(val+'T00:00:00').getTime()+86_400_000)));
  }

  function handleReset() { setStep('form'); setSelectedRoom(null); setAvailableRoomIds(new Set()); setNoRooms(false); }

  async function handleBookSingle(room: CmsRoom) {
    router.push('?contact-booking=true', { scroll: false });
    /*
    setBooking(true);
    try {
      const url = await buildSignedBookingUrl({
        type:     room.slug,
        checkin,  checkout,
        rooms:    1,
        adults:   simpleAdults,
        children: simpleChildren > 0 ? simpleChildren : undefined,
      });
      router.push(url);
    } catch {
      router.push(`/book?type=${room.slug}&checkin=${checkin}&checkout=${checkout}&rooms=1&adults=${simpleAdults}`);
    }
    // booking stays true — overlay persists until the new page mounts and unmounts this component
    */
  }

  async function handleBookMulti() {
    router.push('?contact-booking=true', { scroll: false });
    /*
    const assigned = availRooms.slice(0, roomCount);
    const actualRooms = assigned.length;
    const capA = assigned.reduce((s, r) => s + (r.max_adults   ?? 2), 0);
    const capC = assigned.reduce((s, r) => s + (r.max_children ?? 1), 0);
    const effAdults   = Math.min(simpleAdults,   capA);
    const effChildren = Math.min(simpleChildren, capC);
    const itemMap: Record<string, number> = {};
    for (const r of assigned) itemMap[r.slug] = (itemMap[r.slug] ?? 0) + 1;
    setBooking(true);
    try {
      const url = await buildSignedBookingUrl({
        items:    itemMap,
        checkin,  checkout,
        rooms:    actualRooms,
        adults:   effAdults,
        children: effChildren > 0 ? effChildren : undefined,
      });
      router.push(url);
    } catch {
      router.push(`/book?checkin=${checkin}&checkout=${checkout}&rooms=${actualRooms}&adults=${effAdults}`);
    }
    // booking stays true — overlay persists until the new page mounts
    */
  }

  async function handleSubmit() {
    if (!checkin || !checkout) return;
    setLoading(true); setNoRooms(false);
    try {
      const ids = await fetchAvailableIds(checkin, checkout);
      const bypassAvailability = process.env.isByPassAvailablity === 'true' || process.env.NEXT_PUBLIC_IS_BYPASS_AVAILABLITY === 'true';
      
      if (bypassAvailability) {
        const allIds = new Set<number>();
        roomTypes.forEach(rt => {
          if (rt.external_room_type_id != null) {
            allIds.add(Number(rt.external_room_type_id));
          }
        });
        setAvailableRoomIds(allIds);
        setStep('results');
      } else {
        setAvailableRoomIds(ids);
        if (ids.size === 0) { setNoRooms(true); return; }
        setStep('results');
      }
    } catch { setNoRooms(true); }
    finally  { setLoading(false); }
  }

  const nights = nightsCount();
  const resultsAssigned = availRooms.slice(0, roomCount);
  const resultsPerNight = resultsAssigned.reduce((s, r) => s + r.base_price, 0);
  const confirmedAssigned = availRooms.slice(0, roomCount);
  const confirmedTotal    = confirmedAssigned.reduce((s, r) => s + r.base_price, 0) * nights;
  const selThumb = selectedRoom?.gallery?.[0]?.url ?? null;
  const selAmen1 = selectedRoom?.amenities?.[0]?.label ?? null;
  const selAmen2 = selectedRoom?.amenities?.[1]?.label ?? null;

  /* Effective guest counts capped to confirmed room capacities */
  const confirmedCapA    = confirmedAssigned.reduce((s, r) => s + (r.max_adults   ?? 2), 0);
  const confirmedCapC    = confirmedAssigned.reduce((s, r) => s + (r.max_children ?? 1), 0);
  const effectiveAdults  = Math.min(simpleAdults,   confirmedCapA);
  const effectiveChildren = Math.min(simpleChildren, confirmedCapC);
  const guestDist        = distributeGuests(confirmedAssigned, simpleAdults, simpleChildren);

  /* ── render — ONE persistent panel, AnimatePresence swaps content inside ── */
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      {/* Navigating overlay — shown while router.push is loading the booking page */}
      {booking && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(13, 47, 68, 0.96)',
          backdropFilter: 'blur(10px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: '2rem', padding: '2rem',
        }}>
          <Loader2 size={44} color="rgba(212,175,95,0.9)" strokeWidth={1.4}
            style={{ animation: 'spin 1.1s linear infinite', flexShrink: 0 }} />
          <div style={{ textAlign: 'center', maxWidth: '36rem' }}>
            <p style={{ fontFamily: fonts.serif, fontSize: 'clamp(2rem,4vw,2.6rem)', color: '#fff', fontWeight: 300, letterSpacing: '0.06em', marginBottom: '0.8rem' }}>
              Navigating to booking page
            </p>
            <p style={{ fontFamily: fonts.sans, fontSize: '1.3rem', color: 'rgba(255,255,255,0.65)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Please wait…
            </p>
          </div>
        </div>
      )}

      <motion.div layout transition={layoutTransition} style={panelStyle}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22 }}
          >

            {/* ───────────── FORM ───────────── */}
            {step === 'form' && <>
              <PanelHeading title="Plan Your Stay" />
              <SecDiv />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem', marginBottom:'0.65rem' }}>
                <RoomsDropdown count={roomCount} onChange={handleSetRoomCount} />
                <GuestsDropdown adults={simpleAdults} children={simpleChildren}
                  onAdults={handleSetAdults} onChildren={handleSetChildren}
                  maxAdults={maxAdultsPerRoom} maxChildren={maxChildrenPerRoom} roomCount={roomCount} />
                <DatePicker label="Check-in"  value={checkin}  min={today}       onChange={handleCheckinChange} />
                <DatePicker label="Check-out" value={checkout} min={minCheckout} onChange={setCheckout} alignRight />
              </div>
              {isMultiFlow && (
                <div style={{ marginBottom:'0.65rem', background:'rgba(255,255,255,0.65)',
                  backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)',
                  border:`1px solid ${C.borderRow}`, borderRadius:'16px', padding:'1.1rem 1.25rem' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div>
                      <p style={{ fontFamily:fonts.sans, fontSize:'0.95rem', fontWeight:700, letterSpacing:'0.18em',
                        textTransform:'uppercase', color:colors.gold }}>Selected Stay</p>
                      <h3 style={{ fontFamily:fonts.serif, fontSize:'clamp(1.3rem,2.5vw,2rem)', fontWeight:400,
                        color:C.navy, marginTop:'0.45rem', lineHeight:1.2 }}>
                        {roomCount} Room{roomCount!==1?'s':''} &nbsp;·&nbsp; {totalGuests} Guest{totalGuests!==1?'s':''}
                      </h3>
                    </div>
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'0.5rem', marginTop:'0.85rem' }}>
                    {[
                      `${roomCount} Room${roomCount!==1?'s':''}`,
                      `${simpleAdults} Adult${simpleAdults!==1?'s':''}${simpleChildren>0?` · ${simpleChildren} Child${simpleChildren!==1?'ren':''}` : ''}`,
                      'Flexible Room Selection',
                    ].map(tag => (
                      <span key={tag} style={{ padding:'0.35rem 0.9rem', borderRadius:'999px',
                        background:C.goldPill, fontFamily:fonts.sans, fontSize:'0.95rem', fontWeight:500, color:C.navy }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop:'0.85rem', background:C.noteBg, borderRadius:'12px', padding:'0.75rem 0.9rem' }}>
                    <p style={{ fontFamily:fonts.sans, fontSize:'0.95rem', color:C.muted, lineHeight:1.7 }}>
                      Room types will be selected after availability check. We&apos;ll show the best options for your stay.
                    </p>
                  </div>
                </div>
              )}
              {noRooms && (
                <p style={{ fontFamily:fonts.sans, fontSize:'0.9rem', color:'#b45309',
                  background:'rgba(180,83,9,0.07)', border:'1px solid rgba(180,83,9,0.2)',
                  borderRadius:'8px', padding:'0.65rem 0.9rem', textAlign:'center', marginBottom:'0.65rem' }}>
                  No rooms available for those dates. Please try different dates.
                </p>
              )}
              <button onClick={handleSubmit} disabled={loading} className="btn-primary btn-luxury-shine"
                style={{ width:'100%', opacity:loading?0.75:1, cursor:loading?'wait':'pointer', marginBottom:'0.85rem', gap:'0.5rem' }}>
                {loading ? <><ButtonSpinner color="#fff" size={16} /> Checking…</> : 'Check Availability'}
              </button>
              <TrustBadges />
            </>}

            {/* ───────────── RESULTS ───────────── */}
            {step === 'results' && <>
              <PanelHeading title="Available Rooms" onClose={handleReset} />
              <SecDiv />
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                marginBottom: availRooms.length < roomCount ? '0.6rem' : '1rem', flexWrap:'wrap', gap:'0.4rem' }}>
                <p style={{ fontFamily:fonts.sans, fontSize:'1rem', fontWeight:700, letterSpacing:'0.18em',
                  textTransform:'uppercase', color:C.green, display:'flex', alignItems:'center', gap:'0.4rem' }}>
                  <CheckCircle2 size={13} color={C.green} strokeWidth={2.5} />
                  {availRooms.length} Room{availRooms.length!==1?'s':''} Available
                </p>
                <p style={{ fontFamily:fonts.sans, fontSize:'1rem', color:C.muted2 }}>Prices exclude taxes &amp; fees</p>
              </div>
              {isMultiFlow && availRooms.length < roomCount && availRooms.length > 0 && (
                <div style={{ background:'rgba(180,83,9,0.07)', border:'1px solid rgba(180,83,9,0.2)',
                  borderRadius:'10px', padding:'0.6rem 0.85rem', marginBottom:'0.85rem',
                  fontFamily:fonts.sans, fontSize:'0.95rem', color:'#b45309', lineHeight:1.6 }}>
                  Only {availRooms.length} of {roomCount} rooms available for these dates.
                  {(() => {
                    const capA = availRooms.slice(0, availRooms.length).reduce((s, r) => s + (r.max_adults ?? 2), 0);
                    const capC = availRooms.slice(0, availRooms.length).reduce((s, r) => s + (r.max_children ?? 1), 0);
                    const adjA = Math.min(simpleAdults, capA);
                    const adjC = Math.min(simpleChildren, capC);
                    const changed = adjA !== simpleAdults || adjC !== simpleChildren;
                    if (!changed) return null;
                    return ` Guests adjusted to ${adjA} adult${adjA!==1?'s':''}${adjC>0?` · ${adjC} child${adjC!==1?'ren':''}`:''} (max capacity for available rooms).`;
                  })()}
                </div>
              )}
              <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', overflowY:'auto', maxHeight:'20rem' }}>
                {availRooms.length === 0
                  ? <p style={{ fontFamily:fonts.sans, fontSize:'0.95rem', color:C.muted, textAlign:'center', padding:'1.5rem 0' }}>No rooms found for those dates.</p>
                  : availRooms.map((room, idx) => {
                    const amen1 = room.amenities?.[0]?.label ?? null;
                    const amen2 = room.amenities?.[1]?.label ?? null;
                    const thumb = room.gallery?.[0]?.url ?? null;
                    const autoSel = isMultiFlow && idx < roomCount;
                    return (
                      <div key={room.id} style={{ display:'flex', alignItems:'center', gap:'0.85rem',
                        borderRadius:'14px', border:`1px solid ${C.borderRow}`,
                        background:autoSel ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.7)',
                        padding:'0.75rem',
                        boxShadow: autoSel ? 'inset 0 0 0 1.5px rgba(178,140,72,0.35)' : 'none' }}>
                        <div style={{ width:'6.5rem', height:'5.5rem', flexShrink:0, borderRadius:'10px', overflow:'hidden', background:'rgba(23,50,77,0.06)' }}>
                          {thumb ? <img src={thumb} alt={room.name} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                            : <div style={{ width:'100%', height:'100%', background:'rgba(23,50,77,0.06)' }} />}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <h3 style={{ fontFamily:fonts.serif, fontSize:'clamp(1.5rem,2vw,1.5rem)', fontWeight:400, color:C.navy, lineHeight:1.2, marginBottom:'0.4rem' }}>{room.name}</h3>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.3rem 0.75rem', fontFamily:fonts.sans, fontSize:'0.95rem', color:C.muted }}>
                            <span>{room.max_guests} Guests</span>
                            {amen1 && <span>{amen1}</span>}
                            {amen2 && <span>{amen2}</span>}
                          </div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <p style={{ fontFamily:fonts.sans, fontSize:'0.65rem', letterSpacing:'0.2em', textTransform:'uppercase', color:colors.gold }}>From</p>
                          <p style={{ fontFamily:fonts.serif, fontSize:'clamp(1.3rem,2.5vw,2rem)', color:C.navy, lineHeight:1.15, margin:'0.2rem 0 0.5rem' }}>
                            ₹{room.base_price.toLocaleString('en-IN')}
                          </p>
                          {!isMultiFlow && (
                            <button type="button" className="btn-primary btn-sm"
                              onClick={()=>{ setSelectedRoom(room); setStep('selected'); }}>Select</button>
                          )}
                          {autoSel && (
                            <span style={{ display:'inline-block', padding:'0.2rem 0.65rem', borderRadius:'999px',
                              background:C.greenBg, color:C.green, fontFamily:fonts.sans, fontSize:'0.72rem', fontWeight:500 }}>Selected</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                }
              </div>
              {isMultiFlow && availRooms.length > 0 && <>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  background:'rgba(255,255,255,0.7)', border:`1px solid ${C.borderRow}`, borderRadius:'14px',
                  padding:'0.9rem 1.1rem', marginTop:'0.75rem', marginBottom:'0.75rem' }}>
                  <p style={{ fontFamily:fonts.sans, fontSize:'0.95rem', color:C.muted }}>
                    {Math.min(roomCount, availRooms.length)} Room{roomCount!==1?'s':''} · before taxes
                  </p>
                  <p style={{ fontFamily:fonts.serif, fontSize:'clamp(1.3rem,2.5vw,2rem)', color:C.navy }}>
                    ₹{resultsPerNight.toLocaleString('en-IN')}
                    <span style={{ fontFamily:fonts.sans, fontSize:'0.78rem', color:C.muted, fontWeight:300 }}> /night</span>
                  </p>
                </div>
                <button type="button" className="btn-gold btn-luxury-shine" onClick={()=>setStep('confirmed')}
                  style={{ width:'100%', marginBottom:'0.6rem' }}>Continue Booking</button>
              </>}
              <button type="button" className="btn-ghost-dark"
                onClick={()=>router.push(`/rooms?checkin=${checkin}&checkout=${checkout}`, { scroll:false })}
                style={{ width:'100%', marginTop:isMultiFlow?0:'1rem', background:'none', border:'none' }}>
                View All Rooms →
              </button>
            </>}

            {/* ───────────── SELECTED (single) ───────────── */}
            {step === 'selected' && selectedRoom && <>
              <PanelHeading title="Your Selection" onClose={handleReset} />
              <SecDiv />
              <p style={{ fontFamily:fonts.sans, fontSize:'0.78rem', fontWeight:700, letterSpacing:'0.18em',
                textTransform:'uppercase', color:C.green, display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'1rem' }}>
                <CheckCircle2 size={13} color={C.green} strokeWidth={2.5} /> Room Selected
              </p>
              <div style={{ background:'rgba(255,255,255,0.8)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)',
                borderRadius:'16px', border:`1px solid ${C.borderRow}`, padding:'1rem' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem', marginBottom:'0.85rem' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', minWidth:0 }}>
                    <div style={{ width:'5rem', height:'4rem', flexShrink:0, borderRadius:'10px', overflow:'hidden', background:'rgba(23,50,77,0.06)' }}>
                      {selThumb ? <img src={selThumb} alt={selectedRoom.name} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                        : <div style={{ width:'100%', height:'100%', background:'rgba(23,50,77,0.06)' }} />}
                    </div>
                    <div style={{ minWidth:0 }}>
                      <p style={{ fontFamily:fonts.sans, fontSize:'clamp(1.5rem,2vw,1.5rem)', fontWeight:400, color:C.navy,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedRoom.name}</p>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:'0.25rem 0.6rem', fontFamily:fonts.sans, fontSize:'0.95rem', color:C.muted, marginTop:'0.25rem' }}>
                        <span>{selectedRoom.max_guests} Guests</span>
                        {selAmen1 && <span>{selAmen1}</span>}
                        {selAmen2 && <span>{selAmen2}</span>}
                      </div>
                    </div>
                  </div>
                  <p style={{ fontFamily:fonts.serif, fontSize:'clamp(1.5rem,2vw,1.9rem)', color:C.navy, flexShrink:0 }}>
                    ₹{selectedRoom.base_price.toLocaleString('en-IN')}
                  </p>
                </div>
                <div style={{ borderTop:`1px solid ${C.borderInner}`, paddingTop:'0.85rem' }}>
                  <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
                      <p style={{ fontFamily:fonts.sans, fontSize:'0.65rem', letterSpacing:'0.2em', textTransform:'uppercase', color:colors.gold }}>Total Before Taxes</p>
                      <p style={{ fontFamily:fonts.serif, fontSize:'clamp(1.4rem,3vw,1.95rem)', color:C.navy, marginTop:'0.2rem', lineHeight:1.1 }}>
                        ₹{(selectedRoom.base_price * nights).toLocaleString('en-IN')}
                      </p>
                      <p style={{ fontFamily:fonts.sans, fontSize:'0.95rem', color:C.muted, marginTop:'0.35rem' }}>
                        {fmtShort(checkin)} → {fmtShort(checkout)} · {nights} Night{nights!==1?'s':''}
                      </p>
                    </div>
                    <div style={{ display:'flex', gap:'0.6rem', alignItems:'center' }}>
                      <button type="button" className="btn-gold btn-luxury-shine btn-sm"
                        onClick={()=>handleBookSingle(selectedRoom)} disabled={booking}
                        style={{ flex:1, opacity:booking?0.75:1, cursor:booking?'wait':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', whiteSpace:'nowrap', fontSize:'1.2rem' }}>
                        {booking ? <><ButtonSpinner color="#fff" size={15} /> Navigating…</> : 'Book Now'}
                      </button>
                      <button type="button" className="btn-outline-gold-nav-text btn-sm"
                        onClick={()=>setStep('results')}
                        style={{ whiteSpace:'nowrap', flexShrink:0, fontSize:'1.2rem' }}>Change Room</button>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ marginTop:'1rem' }}><TrustBadges /></div>
            </>}

            {/* ───────────── CONFIRMED (multi) ───────────── */}
            {step === 'confirmed' && <>
              <PanelHeading title="Your Selection" onClose={handleReset} />
              <SecDiv />
              <p style={{ fontFamily:fonts.sans, fontSize:'0.78rem', fontWeight:700, letterSpacing:'0.18em',
                textTransform:'uppercase', color:C.green, display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'1rem' }}>
                <CheckCircle2 size={13} color={C.green} strokeWidth={2.5} /> Rooms Confirmed
              </p>
              <div style={{ background:'rgba(255,255,255,0.8)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)',
                borderRadius:'16px', border:`1px solid ${C.borderRow}`, padding:'1rem' }}>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.85rem', marginBottom:'0.85rem' }}>
                  {confirmedAssigned.map((room, i) => {
                    const thumb  = room.gallery?.[0]?.url ?? null;
                    const amen1  = room.amenities?.[0]?.label ?? null;
                    const amen2  = room.amenities?.[1]?.label ?? null;
                    const isLast = i === confirmedAssigned.length - 1;
                    const rg     = guestDist[i] ?? { adults: 1, children: 0 };
                    const gLine  = `${rg.adults} Adult${rg.adults!==1?'s':''}${rg.children>0?` · ${rg.children} Child${rg.children!==1?'ren':''}` : ''}`;
                    return (
                      <div key={room.id}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', minWidth:0 }}>
                            <div style={{ width:'5rem', height:'4rem', flexShrink:0, borderRadius:'10px', overflow:'hidden', background:'rgba(23,50,77,0.06)' }}>
                              {thumb ? <img src={thumb} alt={room.name} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                                : <div style={{ width:'100%', height:'100%', background:'rgba(23,50,77,0.06)' }} />}
                            </div>
                            <div style={{ minWidth:0 }}>
                              <p style={{ fontFamily:fonts.sans, fontSize:'clamp(1.5rem,2vw,1.5rem)', fontWeight:400, color:C.navy,
                                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>Room {i+1} · {room.name}</p>
                              <div style={{ display:'flex', flexWrap:'wrap', gap:'0.25rem 0.6rem', fontFamily:fonts.sans, fontSize:'0.95rem', color:C.muted, marginTop:'0.25rem' }}>
                                <span>{gLine}</span>
                                {amen1 && <span>{amen1}</span>}
                                {amen2 && <span>{amen2}</span>}
                              </div>
                            </div>
                          </div>
                          <p style={{ fontFamily:fonts.serif, fontSize:'clamp(1.5rem,2vw,1.9rem)', color:C.navy, flexShrink:0 }}>
                            ₹{room.base_price.toLocaleString('en-IN')}
                          </p>
                        </div>
                        {!isLast && <div style={{ height:'1px', background:C.borderInner, margin:'0.85rem 0 0' }} />}
                      </div>
                    );
                  })}
                </div>
                <div style={{ borderTop:`1px solid ${C.borderInner}`, paddingTop:'0.85rem' }}>
                  <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
                      <p style={{ fontFamily:fonts.sans, fontSize:'0.65rem', letterSpacing:'0.2em', textTransform:'uppercase', color:colors.gold }}>Total Before Taxes</p>
                      <p style={{ fontFamily:fonts.serif, fontSize:'clamp(1.4rem,3vw,1.95rem)', color:C.navy, marginTop:'0.2rem', lineHeight:1.1 }}>
                        ₹{confirmedTotal.toLocaleString('en-IN')}
                      </p>
                      <p style={{ fontFamily:fonts.sans, fontSize:'0.95rem', color:C.muted, marginTop:'0.35rem' }}>
                        {fmtShort(checkin)} → {fmtShort(checkout)} · {nights} Night{nights!==1?'s':''}
                      </p>
                      <p style={{ fontFamily:fonts.sans, fontSize:'0.9rem', color:C.muted2, marginTop:'0.2rem' }}>
                        {confirmedAssigned.length} Room{confirmedAssigned.length!==1?'s':''} · {effectiveAdults} Adult{effectiveAdults!==1?'s':''}{effectiveChildren>0?` · ${effectiveChildren} Child${effectiveChildren!==1?'ren':''}` : ''}
                      </p>
                    </div>
                    <div style={{ display:'flex', gap:'0.6rem', alignItems:'center' }}>
                      <button type="button" className="btn-gold btn-luxury-shine btn-sm"
                        onClick={handleBookMulti} disabled={booking}
                        style={{ flex:1, opacity:booking?0.75:1, cursor:booking?'wait':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', whiteSpace:'nowrap', fontSize:'1.2rem' }}>
                        {booking ? <><ButtonSpinner color="#fff" size={15} /> Navigating…</> : 'Book Now'}
                      </button>
                      <button type="button" className="btn-outline-gold-nav-text btn-sm"
                        onClick={()=>setStep('results')}
                        style={{ whiteSpace:'nowrap', flexShrink:0, fontSize:'1.2rem' }}>Change Rooms</button>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ marginTop:'1rem' }}><TrustBadges /></div>
            </>}

          </motion.div>
        </AnimatePresence>
      </motion.div>
    </>
  );
}
